import { ENDPOINTS } from 'config/endpoints';
import { useAuth } from 'context/auth';
import { invalidateEditProspectQueries } from 'hooks/services/prospect/useEditProspect';
import useGetActiveScrapeBulksAnalytics from 'hooks/services/scrape/useGetActiveScrapeBulksAnalytics';
import { FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConditionalSnackBar } from '../snackbar';
import { invalidateEditAccountQueries } from 'hooks/services/account/useEditAccount';
import { PollerContext } from './contexts';

const PollerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { enabledContextCalls } = useAuth();

  const [prospectsEnrichmentStarted, setProspectsEnrichmentStarted] =
    useState(false);
  const [prospectsEnrichmentCompleted, setProspectsEnrichmentCompleted] =
    useState(false);
  const [companiesEnrichmentStarted, setCompaniesEnrichmentStarted] =
    useState(false);
  const [companiesEnrichmentCompleted, setCompaniesEnrichmentCompleted] =
    useState(false);

  const {
    data: activeScrapeBulkAnalytics,
    refetch: refetchGetActiveScrapeBulksAnalytics,
  } = useGetActiveScrapeBulksAnalytics(enabledContextCalls);

  const queryClient = useQueryClient();
  useEffect(() => {
    let t: NodeJS.Timeout;

    const prospectScrapeAnalytics =
      activeScrapeBulkAnalytics?.data?.data?.scrapeBulkProspect;
    const howManyProspectsOngoing = prospectScrapeAnalytics
      ? prospectScrapeAnalytics.total - prospectScrapeAnalytics.completed
      : undefined;
    const companyScrapeAnalytics =
      activeScrapeBulkAnalytics?.data?.data?.scrapeBulkCompany;
    const howManyCompaniesOngoing = companyScrapeAnalytics
      ? companyScrapeAnalytics.total - companyScrapeAnalytics.completed
      : undefined;

    let hasToInvalidateProspectSearches = false;
    let hasToInvalidateAccountSearches = false;

    if (howManyProspectsOngoing || howManyCompaniesOngoing) {
      if (howManyProspectsOngoing) {
        hasToInvalidateProspectSearches = true;
      }
      if (howManyCompaniesOngoing) {
        hasToInvalidateAccountSearches = true;
      }
      t = setTimeout(() => {
        refetchGetActiveScrapeBulksAnalytics();
      }, 5000);
    }

    if (!howManyProspectsOngoing && prospectsEnrichmentStarted) {
      hasToInvalidateProspectSearches = true;
      setProspectsEnrichmentCompleted(true);
      setProspectsEnrichmentStarted(false);
      setTimeout(() => {
        setProspectsEnrichmentCompleted(false);
      }, 2000);
    }

    if (!howManyCompaniesOngoing && companiesEnrichmentStarted) {
      hasToInvalidateAccountSearches = true;
      setCompaniesEnrichmentCompleted(true);
      setCompaniesEnrichmentStarted(false);
      setTimeout(() => {
        setCompaniesEnrichmentCompleted(false);
      }, 2000);
    }

    if (hasToInvalidateAccountSearches || hasToInvalidateProspectSearches) {
      if (hasToInvalidateProspectSearches) {
        invalidateEditProspectQueries(queryClient);
      }

      if (hasToInvalidateAccountSearches) {
        invalidateEditAccountQueries(queryClient);
      }

      queryClient.invalidateQueries([ENDPOINTS.user.credits]);
    }

    return () => {
      clearTimeout(t);
    };
  }, [activeScrapeBulkAnalytics]);

  useConditionalSnackBar([
    {
      condition: companiesEnrichmentCompleted,
      message: 'Companies enrichment successfully completed',
      severity: 'success',
    },
    {
      condition: prospectsEnrichmentCompleted,
      message: 'Contacts enrichment successfully completed',
      severity: 'success',
    },
  ]);

  return (
    <PollerContext.Provider
      value={{
        pollingProspectsEnrichInBulk:
          prospectsEnrichmentStarted && !prospectsEnrichmentCompleted,
        pollingCompaniesEnrichInBulk:
          companiesEnrichmentStarted && !companiesEnrichmentCompleted,
        setCompaniesEnrichmentStarted,
        setProspectsEnrichmentStarted,
      }}
    >
      {children}
    </PollerContext.Provider>
  );
};

export default PollerProvider;

export function usePollerContext() {
  const context = useContext(PollerContext);
  if (context === undefined) {
    throw new Error(`usePoller must be used within a PollerProvider`);
  }

  return context;
}
